import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, ImagePlus, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

const MAX_IMAGE_MB = 5;
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Carica una foto nel bucket pubblico site-images e ne restituisce l'URL;
 * null (con un avviso) se il file non va bene o il caricamento fallisce.
 */
async function uploadSiteImage(file: File, prefix: string): Promise<string | null> {
  if (!IMAGE_TYPES.includes(file.type)) {
    toast.error('Carica una foto JPG, PNG o WebP');
    return null;
  }
  if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
    toast.error(`La foto supera ${MAX_IMAGE_MB} MB: riducila e riprova`);
    return null;
  }
  try {
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    // Nome sempre nuovo: browser e CDN non mostrano mai la foto vecchia.
    const path = `${prefix.replace(/[^a-z0-9-]/gi, '-')}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;
    const { error } = await supabase.storage.from('site-images').upload(path, file, { cacheControl: '31536000' });
    if (error) throw error;
    return supabase.storage.from('site-images').getPublicUrl(path).data.publicUrl;
  } catch (err: any) {
    toast.error(`Caricamento non riuscito: ${err.message}`);
    return null;
  }
}

/** Input file nascosto, aperto dai pulsanti "Sostituisci" / "Aggiungi una foto". */
function useFilePicker(onFiles: (files: File[]) => Promise<void>, multiple = false) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const input = (
    <input
      ref={inputRef}
      type="file"
      accept={IMAGE_TYPES.join(',')}
      multiple={multiple}
      className="hidden"
      onChange={async (e) => {
        const files = Array.from(e.target.files ?? []);
        if (files.length === 0) return;
        setUploading(true);
        await onFiles(files);
        setUploading(false);
        e.target.value = '';
      }}
    />
  );

  return { input, uploading, open: () => inputRef.current?.click() };
}

const secondaryButton =
  'flex h-9 items-center gap-1.5 rounded-lg bg-muted px-3 text-[13px] font-semibold text-foreground hover:bg-muted/70 disabled:opacity-60';

/** Una foto: caricata da te, oppure `fallback` (la predefinita) se null. */
export const ImageField: React.FC<{
  label: string;
  value: string | null;
  /** Foto mostrata quando non ne è stata caricata una; senza, il campo può restare vuoto. */
  fallback?: string;
  prefix: string;
  hint?: string;
  onChange: (url: string | null) => void;
}> = ({ label, value, fallback, prefix, hint, onChange }) => {
  const picker = useFilePicker(async ([file]) => {
    const url = await uploadSiteImage(file, prefix);
    if (url) onChange(url);
  });
  const shown = value ?? fallback;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <div className="flex flex-wrap items-center gap-4">
        {shown ? (
          <img src={shown} alt="" className="h-24 w-40 flex-none rounded-lg object-cover ring-1 ring-border" />
        ) : (
          <div className="flex h-24 w-40 flex-none items-center justify-center rounded-lg bg-muted text-muted-foreground ring-1 ring-border">
            <ImagePlus className="h-6 w-6" />
          </div>
        )}
        <div className="flex flex-col items-start gap-2">
          <span className="text-xs text-muted-foreground">
            {value ? 'Foto caricata da te' : fallback ? 'Foto predefinita' : 'Nessuna foto'}
          </span>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={picker.open} disabled={picker.uploading} className={secondaryButton}>
              <ImagePlus className="h-4 w-4" />
              {picker.uploading ? 'Carico…' : value || fallback ? 'Sostituisci' : 'Carica una foto'}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange(null)}
                className="h-9 rounded-lg px-3 text-[13px] font-semibold text-muted-foreground hover:bg-muted"
              >
                {fallback ? 'Usa la predefinita' : 'Togli la foto'}
              </button>
            )}
          </div>
        </div>
      </div>
      <span className="text-xs leading-5 text-muted-foreground">
        {hint ?? `Orizzontale, almeno 1600 px di larghezza, massimo ${MAX_IMAGE_MB} MB.`}
      </span>
      {picker.input}
    </div>
  );
};

/** Più foto in fila, riordinabili: una sola si vede a tutta larghezza, di più affiancate. */
export const ImageListField: React.FC<{
  label: string;
  value: string[];
  max: number;
  prefix: string;
  onChange: (urls: string[]) => void;
}> = ({ label, value, max, prefix, onChange }) => {
  const picker = useFilePicker(async (files) => {
    const urls: string[] = [];
    for (const file of files.slice(0, max - value.length)) {
      const url = await uploadSiteImage(file, prefix);
      if (url) urls.push(url);
    }
    if (files.length > max - value.length) toast.error(`Al massimo ${max} foto per paragrafo`);
    if (urls.length) onChange([...value, ...urls]);
  }, true);

  const moveTo = (from: number, to: number) => {
    const next = [...value];
    const [url] = next.splice(from, 1);
    next.splice(to, 0, url);
    onChange(next);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-3">
        {value.map((url, i) => (
          <div key={url} className="flex flex-col gap-1">
            <img src={url} alt="" className="h-20 w-32 rounded-lg object-cover ring-1 ring-border" />
            <div className="flex justify-center gap-0.5">
              <button
                type="button"
                aria-label="Sposta a sinistra"
                disabled={i === 0}
                onClick={() => moveTo(i, i - 1)}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Togli la foto"
                onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                aria-label="Sposta a destra"
                disabled={i === value.length - 1}
                onClick={() => moveTo(i, i + 1)}
                className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted disabled:opacity-40"
              >
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
        {value.length < max && (
          <button
            type="button"
            onClick={picker.open}
            disabled={picker.uploading}
            className="flex h-20 w-32 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-border text-xs font-semibold text-muted-foreground hover:bg-muted disabled:opacity-60"
          >
            <ImagePlus className="h-5 w-5" />
            {picker.uploading ? 'Carico…' : 'Aggiungi foto'}
          </button>
        )}
      </div>
      <span className="text-xs leading-5 text-muted-foreground">
        Fino a {max} foto, massimo {MAX_IMAGE_MB} MB l'una. Puoi sceglierne più di una insieme.
      </span>
      {picker.input}
    </div>
  );
};
